import { ChevronDown, ExternalLink } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import { useReportSectionGroup } from './ReportSectionGroup';
import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import StatusChip, { QualityChip } from '../StatusChip';
import { tx } from '../../i18n';
import { formatDate } from '../../lib/format';
import type { Quality, Status } from '../../lib/status';

/** Where a section's numbers come from and when they were last read. */
export interface SectionSource {
  /** The register or dataset, named as its publisher names it. */
  name: string;
  /** When the source last published, ISO. */
  published?: string | null;
  /** When this report read it, ISO. */
  checked?: string | null;
  /** The published version or release, when the dataset has one. */
  version?: string | null;
  href?: string | null;
}

interface ReportSectionProps {
  id: string;
  icon: LucideIcon;
  title: string;
  /** One line under the title: what this section covers and whether it is scored. */
  note?: ReactNode;
  /** A score, a band, a price: whatever this section is summed up by. */
  aside?: ReactNode;
  /** The section's verdict in the one vocabulary the whole report uses. */
  status?: Status;
  /** Overrides the status chip's word, when the section says it better. */
  statusLabel?: string;
  /** How far the section's numbers can be trusted. */
  quality?: Quality;
  /**
   * True for a section that is shown but not scored. Five sections said "Not part of the score."
   * in their own note; one small label in the header says it once, in the same place every time.
   */
  informational?: boolean;
  /** Shown at the foot of the section, in the same shape on every section. */
  source?: SectionSource | null;
  children: ReactNode;
}

/**
 * The shell every section of the report shares.
 *
 * <p>The page is one column of these rather than tabs and nested cards. Every one starts closed, so
 * the whole report arrives as a list a reader takes in at once and then opens what they care
 * about; the headline, the score and the key facts above already say what the report found. A
 * link to a section's id - from the contents beside the page, or from a link somebody was sent -
 * opens it and scrolls to it, so a closed section never swallows a deep link.
 *
 * <p>Inside a {@code ReportSectionGroup} only one is open at a time, which is what keeps the page
 * roughly the length of the thing being read. Outside one it keeps its own state, so a section
 * used on its own still opens and closes.
 *
 * <p>The closed row carries the section's status and the reliability of its data, because those
 * are exactly what a reader needs in order to decide whether to open it at all.
 */
export default function ReportSection({
  id,
  icon: Icon,
  title,
  note,
  aside,
  status,
  statusLabel,
  quality,
  informational = false,
  source,
  children,
}: ReportSectionProps) {
  const group = useReportSectionGroup();
  const [localOpen, setLocalOpen] = useState(false);
  const heading = useRef<HTMLHeadingElement>(null);
  const open = group ? group.openId === id : localOpen;
  const bodyId = `${useId()}-body`;

  const setOpen = (next: boolean, anchor?: HTMLElement | null) => {
    if (group) {
      group.open(next ? id : null, anchor);
    } else {
      setLocalOpen(next);
    }
  };

  // The deep-link effect must run when the hash points here, and at no other time. Reaching the
  // group through a ref keeps it out of the dependencies: the group's value changes every time
  // any section opens, and with it in the deps, closing this one re-ran the effect, which saw the
  // hash still pointing here and opened it straight back up.
  const openRef = useRef(setOpen);
  openRef.current = setOpen;

  useEffect(() => {
    const openIfTargeted = () => {
      if (window.location.hash !== `#${id}`) {
        return;
      }
      openRef.current(true);
      // After the body is laid out, or the browser scrolls to where the section used to end.
      window.requestAnimationFrame(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    };
    openIfTargeted();
    window.addEventListener('hashchange', openIfTargeted);
    return () => window.removeEventListener('hashchange', openIfTargeted);
  }, [id]);

  return (
    <section
      id={id}
      className={open ? 'report-section report-section--open' : 'report-section'}
      data-tour="section"
    >
      <h2 className="report-section__head" ref={heading}>
        <button
          type="button"
          className="report-section__toggle"
          aria-expanded={open}
          aria-controls={bodyId}
          onClick={() => setOpen(!open, heading.current)}
        >
          <span className="report-section__icon">
            <Icon size={18} aria-hidden="true" />
          </span>
          <span className="report-section__titles">
            <span className="report-section__title">{title}</span>
            {note ? <span className="small muted">{note}</span> : null}
          </span>
          <span className="report-section__marks">
            {informational ? (
              <span
                className="info-only"
                title={tx(
                  'Shown because it is worth knowing, but it does not change the score.',
                  'Bilmeye değer olduğu için gösteriliyor; puanı değiştirmez.',
                  'Pokazane, bo warto to wiedzieć, ale nie wpływa na ocenę.',
                  'Mostrado por ser útil saber, mas não altera a pontuação.',
                  'Se muestra porque conviene saberlo, pero no cambia la puntuación.',
                )}
              >
                {tx('Information only', 'Yalnızca bilgi', 'Tylko informacyjnie', 'Apenas informativo', 'Solo informativo')}
              </span>
            ) : null}
            {status ? <StatusChip status={status} label={statusLabel} /> : null}
            {aside ? <span className="report-section__aside">{aside}</span> : null}
          </span>
          <ChevronDown size={20} aria-hidden="true" className="report-section__chevron" />
          <span className="sr-only">
            {open
              ? tx('Collapse', 'Kapat', 'Zwiń', 'Fechar', 'Contraer')
              : tx('Expand', 'Aç', 'Rozwiń', 'Abrir', 'Desplegar')}
          </span>
        </button>
      </h2>
      <div id={bodyId} className="report-section__body" hidden={!open}>
        {children}
        {source || quality ? <SourceFooter source={source} quality={quality} /> : null}
      </div>
    </section>
  );
}

/**
 * Where this section's numbers came from, in the same shape on every section.
 *
 * <p>"Source: EPC Register · Checked 22 Sep 2026" answers the two questions a tenant has about a
 * number they are about to repeat to an agent: who says so, and how old is it.
 */
export function SourceFooter({ source, quality }: { source?: SectionSource | null; quality?: Quality }) {
  const parts: string[] = [];
  if (source?.published) {
    parts.push(
      tx(
        `Published ${formatDate(source.published)}`,
        `Yayım ${formatDate(source.published)}`,
        `Opublikowano ${formatDate(source.published)}`,
        `Publicado em ${formatDate(source.published)}`,
        `Publicado el ${formatDate(source.published)}`,
      ),
    );
  }
  if (source?.version) {
    parts.push(source.version);
  }
  if (source?.checked) {
    parts.push(
      tx(
        `Checked ${formatDate(source.checked)}`,
        `Kontrol ${formatDate(source.checked)}`,
        `Sprawdzono ${formatDate(source.checked)}`,
        `Verificado em ${formatDate(source.checked)}`,
        `Comprobado el ${formatDate(source.checked)}`,
      ),
    );
  }

  return (
    <p className="source-line small muted" data-tour="provenance">
      {quality ? <QualityChip quality={quality} /> : null}
      {source ? (
        <span>
          {tx('Source', 'Kaynak', 'Źródło', 'Fonte', 'Fuente')}:{' '}
          {source.href ? (
            <a href={source.href} target="_blank" rel="noreferrer">
              {source.name} <ExternalLink size={11} aria-hidden="true" />
            </a>
          ) : (
            source.name
          )}
          {parts.length > 0 ? ` · ${parts.join(' · ')}` : ''}
        </span>
      ) : null}
    </p>
  );
}
